import config from '../config';

// The dashboard's half of the MCP sign-in (backend metaplan A7c; AIE plan §6 "Login handoff").
// Contract: apiv2/authserver.openapi.yaml — POST /v2/edit/oauth/handoff/ {req} →
// {code, expires_in, redirect_url, req_id}; refusals are the M9 envelope {code, message, …}.
//
// The authorization server sends a tenant user's browser to /oauth/handoff?req=<signed request>.
// The tenant's Auth0 sign-in drops the query string on its round trip, so `req` is moved into
// this tab's sessionStorage before anything else renders, and read back once there is a session.

export const OAUTH_HANDOFF_PATH = '/oauth/handoff';

const STASH_KEY = 'cp_oauth_handoff';
// A handoff request lives at most 600 s (contract); an older stash is useless.
export const STASH_TTL_MS = 10 * 60 * 1000;

// sessionStorage can throw (blocked storage, some private modes): the handoff then simply
// finds no stash and says the link is incomplete.
function storage() {
    try {
        return window.sessionStorage;
    } catch (_e) {
        return null;
    }
}

// Move `req` from the address bar into this tab's sessionStorage and drop it from the URL,
// keeping every other parameter (the Auth0 callback's `code` and `state`). Idempotent.
export function stashRequestFromUrl(now = Date.now()) {
    const url = new URL(window.location.href);
    const req = url.searchParams.get('req');
    if (!req) { return; }
    try {
        storage()?.setItem(STASH_KEY, JSON.stringify({ req, at: now }));
    } catch (_e) {
        // Nothing to do: the page will report the link as incomplete.
    }
    url.searchParams.delete('req');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

function readStash(now) {
    let stash = null;
    try {
        stash = JSON.parse(storage()?.getItem(STASH_KEY) || 'null');
    } catch (_e) {
        stash = null;
    }
    if (!stash || typeof stash.req !== 'string' || !stash.req) { return null; }
    if (typeof stash.at !== 'number' || now - stash.at > STASH_TTL_MS) {
        clearStashedRequest();
        return null;
    }
    return stash;
}

// The stashed `req`, or null when there is none or it is too old to be accepted.
export function readStashedRequest(now = Date.now()) {
    const stash = readStash(now);
    return stash ? stash.req : null;
}

// A 401 signs the stale session out and comes back — once per request. A second 401 would loop
// (the tenant's Auth0 sign-in continues by itself), so it is reported instead. True when this
// is the first one, and records it.
export function takeSignOutRetry(now = Date.now()) {
    const stash = readStash(now);
    if (!stash || stash.signedOut) { return false; }
    try {
        storage()?.setItem(STASH_KEY, JSON.stringify({ ...stash, signedOut: true }));
    } catch (_e) {
        return false;
    }
    return true;
}

export function clearStashedRequest() {
    try {
        storage()?.removeItem(STASH_KEY);
    } catch (_e) {
        // ignore
    }
}

// POST the handoff with the dashboard session token. Resolves {status, body} (body: the parsed
// JSON or null); rejects only on a network error. Plain fetch, not the data provider: its
// 401/403 handler signs the user out, and a 403 here (not_eligible…) is an answer, not an
// expired session.
export async function postHandoff(token, req) {
    const res = await fetch(`${config.apiUrl}/oauth/handoff/`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ req }),
    });
    const body = await res.json().catch(() => null);
    return { status: res.status, body };
}

// Only an absolute https URL is followed (http too where allowHttp: local development); a
// `javascript:` URL, a relative path or garbage is refused rather than navigated to.
export function handoffRedirectTarget(raw, { allowHttp = false } = {}) {
    if (typeof raw !== 'string') { return null; }
    let url;
    try {
        url = new URL(raw);
    } catch (_e) {
        return null;
    }
    if (url.protocol === 'https:' || (allowHttp && url.protocol === 'http:')) { return url.toString(); }
    return null;
}

// The franchise path (backend plan "Tenant-bound MCP URLs" §4.7, P7). The authorization server sends a sign-in
// for a licensee's MCP address to the licensee's dashboard; franchise (parent) staff sign in at their own company's
// dashboard instead, so they type its code here and continue there with the same `req` -- the backend binds them to
// the licensee only when their account is assigned to it (else tenant_mismatch, the same copy for every case). The
// code is checked as a host label and never looked up: nothing on this page may tell whether a company exists or is
// this one's franchise (§4.9).

// A company code in a host: a lowercase DNS label (the authorization server's TENANT_CODE_RE, plan D1).
const COMPANY_CODE_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export function normalizeCompanyCode(raw) {
    return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

export function isCompanyCode(code) {
    return typeof code === 'string' && COMPANY_CODE_RE.test(code);
}

// This page is `<company code>.<the dashboard domain>`, the domain pinned per build (`config.dashboardDomain`:
// production `dashboard.curatedplanet.com`, staging `dashboard-staging.curatedplanet.com`, local `cp.devel:3000` --
// the authorization server's dashboard handoff addresses). Only there is the franchise path offered, so a page served
// under any other name (the bare dashboard host, a CloudFront or S3 name, a deeper or foreign host) never carries a
// `req` anywhere. The page's real address, not `config.domainOverride`. False when the build pins no domain.
export function onCompanyDashboard(location = window.location, domain = config.dashboardDomain) {
    if (typeof domain !== 'string' || !domain) { return false; }
    const host = String((location && location.host) || '').toLowerCase();
    const suffix = `.${domain.toLowerCase()}`;
    return host.endsWith(suffix) && isCompanyCode(host.slice(0, -suffix.length));
}

// `<scheme>://<code>.<the dashboard domain>/oauth/handoff?req=<req>`: the `req` exactly as received, URL-encoded once,
// and no other parameter. Null unless this page is on a company's dashboard, the code is a company code and there is
// a `req`.
export function franchiseHandoffUrl(code, req, location = window.location, domain = config.dashboardDomain) {
    if (!onCompanyDashboard(location, domain) || !isCompanyCode(code) || typeof req !== 'string' || !req) { return null; }
    return `${location.protocol}//${code}.${domain.toLowerCase()}${OAUTH_HANDOFF_PATH}?req=${encodeURIComponent(req)}`;
}

const RESTART = ' Go back to your MCP client and connect again.';
const UNREACHABLE = { message: 'The dashboard could not complete the sign-in. Try again.', retry: true };

// Copy per failure code: the backend's envelope codes for the dashboard handoff, plus the
// page's own (CLIENT_FAILURES).
export const HANDOFF_FAILURES = {
    invalid_request: { message: `This sign-in link is invalid or has expired.${RESTART}` },
    // Also a configuration drift (the backend's MCP address differs from the sign-in server's), which retrying
    // does not fix.
    invalid_handoff_request: { message: `This sign-in link is invalid or has expired.${RESTART} If this keeps happening, contact Curated Planet support.` },
    handoff_request_used: { message: `This sign-in link was already used.${RESTART}` },
    not_eligible: { message: 'Your account has no permission that MCP tools need. Ask your administrator for access, then connect again from your MCP client.' },
    // Tenant-bound MCP URLs: the company comes from the MCP address the client was given (its `resource`). Unknown and
    // foreign codes and a licensee a franchise user is not assigned to (P7, on the franchise's dashboard) answer alike
    // (D7), so the copy names no company, claims none of the three and gives the remedy for each.
    // `mcpConnections`: the page links to the user's own address.
    tenant_mismatch: {
        message: "This MCP address is not available to your account. Use your own company's address, shown on MCP connections. "
            + "Franchise staff: ask your franchise's administrator to assign you to the company this address is for, then connect again.",
        mcpConnections: true,
    },
    tenant_required: { message: 'You are signed in with a Curated Planet ops account. Ops accounts connect MCP clients from the Ops Console.' },
    authserver_disabled: { message: 'MCP sign-in is not set up on this server.' },
    // The page's own:
    session_rejected: { message: 'The server did not accept your session, even after signing in again. Sign out, sign in, then connect again from your MCP client.' },
    missing_request: { message: `This sign-in link is incomplete or has expired.${RESTART}` },
    unexpected_redirect: { message: `The server answered with an unexpected sign-in address, so the dashboard did not follow it.${RESTART}` },
    unreachable: UNREACHABLE,
};

export const CLIENT_FAILURES = ['session_rejected', 'missing_request', 'unexpected_redirect', 'unreachable'];

const NOT_ALLOWED = { message: 'This account cannot connect MCP clients.' };

export function describeHandoffFailure(code, status) {
    return HANDOFF_FAILURES[code] || (status === 403 ? NOT_ALLOWED : UNREACHABLE);
}

// The `req` is dead after these answers; any other refusal keeps the stash until it expires.
export function requestIsSpent(status) {
    return status === 400 || status === 409;
}
